/**
 * BreathingGuide — the active guided breathing exercise.
 *
 * Uses the typed audioController (Web Audio) and sessionController
 * (rAF + clock) for synced playback. Ported from the standalone Calm The
 * Body app (GuideScreen.jsx).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createSession } from '@/services/calm/sessionController';
import type { SessionController, Tick } from '@/services/calm/sessionController';
import {
  unlock,
  preload,
  warmBuffers,
  setVoice,
  handleVisibilityChange,
  stopSession,
} from '@/services/calm/audioController';
import { getProtocol } from '@/data/calm/protocols';
import type { PhaseType } from '@/data/calm/protocols';
import { useWakeLock } from '@/hooks/useWakeLock';

interface PhaseVisual {
  scale: number;
  glow: number;
}
const PHASE_VISUALS: Record<PhaseType, PhaseVisual> = {
  inhale: { scale: 1.0, glow: 1.0 },
  top_up: { scale: 1.05, glow: 1.0 },
  hold_top: { scale: 1.0, glow: 0.7 },
  hold_bottom: { scale: 0.6, glow: 0.3 },
  exhale: { scale: 0.6, glow: 0.3 },
  rest: { scale: 0.55, glow: 0.2 },
};

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function BreathingGuide({
  protocolId,
  minutes,
  voice,
  onExit,
  onComplete,
  onNaturalComplete,
}: {
  protocolId: string;
  minutes: number;
  voice: string;
  onExit: () => void;
  onComplete: () => void;
  /** Optional: fires only when the timed session runs to its end (not on "End session"). */
  onNaturalComplete?: () => void;
}) {
  const protocol = getProtocol(protocolId);
  const duration = minutes * 60;

  // Keep the screen awake for the whole guided session.
  useWakeLock(true);

  const [tick, setTick] = useState<Tick | null>(null);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const sessionRef = useRef<SessionController | null>(null);
  const naturalCompleteRef = useRef(onNaturalComplete);
  naturalCompleteRef.current = onNaturalComplete;

  // Lock in the chosen voice for this session.
  useEffect(() => {
    setVoice(voice);
  }, [voice]);

  // Preload audio on mount (best-effort).
  useEffect(() => {
    let cancelled = false;
    preload().then(() => {
      if (!cancelled) setAudioReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Visibility change handler.
  //
  // When the screen sleeps or the tab is backgrounded, mobile browsers SUSPEND
  // Web Audio and pause requestAnimationFrame — and they won't let us resume the
  // audio context again without a fresh user gesture. A silent auto-resume on
  // return therefore left the orb frozen and the cues silent ("wouldn't resume").
  // So on hide we PAUSE the running session; on return it stays paused showing
  // "Resume", and the user's tap (a gesture) resumes audio + timing reliably.
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        if (sessionRef.current?.getState() === 'running') {
          sessionRef.current.pause();
          setPaused(true);
        }
      } else {
        handleVisibilityChange();
        sessionRef.current?.handleVisibility();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Create session.
  useEffect(() => {
    if (!protocol) return;
    const session = createSession(protocol, duration, (s) => setTick(s), () => { naturalCompleteRef.current?.(); onComplete(); });
    sessionRef.current = session;
    return () => {
      session.stop();
      stopSession();
    };
  }, [protocol, duration, onComplete]);

  // 3-2-1 countdown.
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      setCountdown(null);
      sessionRef.current?.start();
      setStarted(true);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleStart = useCallback(() => {
    unlock();
    warmBuffers();
    setCountdown(3);
  }, []);

  const handlePause = useCallback(() => {
    if (paused) {
      sessionRef.current?.resume();
      setPaused(false);
    } else {
      sessionRef.current?.pause();
      setPaused(true);
    }
  }, [paused]);

  const handleEndRequest = useCallback(() => {
    sessionRef.current?.stop();
    stopSession();
    onComplete();
  }, [onComplete]);

  const handleToggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      sessionRef.current?.setSound(next);
      return next;
    });
  }, []);

  if (!protocol) {
    return (
      <div className="cb-guide-screen cb-guide-intro cb-fade-in">
        <p className="cb-guide-intro-tip">This exercise is unavailable.</p>
        <button className="cb-guide-intro-start-link" onClick={onExit}>
          Go back
        </button>
      </div>
    );
  }

  // Orb visuals.
  const phase = tick?.phase;
  const phaseType: PhaseType = phase?.type ?? 'rest';
  const target = PHASE_VISUALS[phaseType];
  const cycleProgress = tick?.cycleProgress ?? 0;

  let orbScale: number;
  if (phaseType === 'inhale' || phaseType === 'top_up') {
    orbScale = 0.6 + (target.scale - 0.6) * cycleProgress;
  } else if (phaseType === 'exhale') {
    orbScale = 1.0 - (1.0 - target.scale) * cycleProgress;
  } else {
    orbScale = target.scale;
  }

  const rawCountdown = tick ? Math.ceil(tick.phaseRemaining) : 0;
  const phaseCountdown = tick?.phase ? Math.min(rawCountdown, tick.phase.seconds) : rawCountdown;
  const progressPct = tick ? Number((tick.progress * 100).toFixed(1)) : 0;
  const remaining = tick ? Math.max(0, Math.ceil(tick.totalDuration - tick.elapsed)) : duration;

  // ── PRE-START INTRO ──
  if (!started && countdown === null) {
    return (
      <div className="cb-guide-screen cb-guide-intro cb-fade-in">
        <button className="cb-back" onClick={onExit} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        <div className="cb-guide-intro-content">
          {protocol.introText && <p className="cb-guide-intro-tip">{protocol.introText}</p>}

          {protocol.sighIntro && (
            <div className="cb-guide-sigh-prep-inline">
              <div className="cb-guide-sigh-steps--airy">
                {protocol.sighIntro.steps.map((step, i) => (
                  <div key={i} className="cb-guide-sigh-step">
                    <span className="cb-guide-sigh-step-num">{i + 1}</span>
                    <span className="cb-guide-sigh-step-text">{step}</span>
                  </div>
                ))}
              </div>
              {protocol.sighIntro.note && <p className="cb-guide-sigh-note">{protocol.sighIntro.note}</p>}
            </div>
          )}

          <button className="cb-guide-intro-start-link" onClick={handleStart} disabled={!audioReady}>
            {audioReady ? 'Start' : 'Loading audio...'}
          </button>

          <p className="cb-guide-safety">
            Stop at any point if you feel unwell.
            <br />
            Return to your normal breathing.
          </p>
        </div>
      </div>
    );
  }

  // ── COUNTDOWN ──
  if (countdown !== null) {
    return (
      <div className="cb-guide-screen cb-guide-countdown-overlay cb-fade-in">
        <p className="cb-guide-countdown-label">STARTING IN</p>
        <span key={countdown} className="cb-guide-countdown-number cb-fade-in">
          {countdown}
        </span>
      </div>
    );
  }

  // ── ACTIVE SESSION ──
  return (
    <div className="cb-guide-screen cb-guide-active cb-fade-in">
      <div className="cb-guide-top-bar">
        <button className="cb-guide-icon-btn" onClick={handleEndRequest} aria-label="End session" title="End session">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          </svg>
        </button>

        <div className="cb-guide-time-remaining" aria-live="off">
          {formatTime(remaining)}
        </div>

        <div className="cb-guide-sound-group">
          {soundOn && (
            <span className="cb-guide-silent-hint" aria-hidden="true">
              Phone off silent
            </span>
          )}
          <button
            className={`cb-guide-icon-btn ${soundOn ? 'cb-guide-icon-btn--active' : ''}`}
            onClick={handleToggleSound}
            aria-label={soundOn ? 'Mute sounds' : 'Enable sounds'}
            title={soundOn ? 'Mute sounds' : 'Enable sounds'}
          >
            {soundOn ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path d="M11 5L6 9H2v6h4l5 4V5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path d="M11 5L6 9H2v6h4l5 4V5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                <line x1="23" y1="9" x2="17" y2="15" strokeWidth="2" strokeLinecap="round" />
                <line x1="17" y1="9" x2="23" y2="15" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div
        className="cb-guide-progress-track"
        role="progressbar"
        aria-valuenow={Math.round(progressPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Session progress"
      >
        <div className="cb-guide-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="cb-guide-orb-area" aria-hidden="true">
        <div className="cb-guide-orb-container">
          <div className="cb-guide-orb-ring" />
          <div
            className={`cb-guide-orb${protocol.slowAnimation ? ' cb-guide-orb--slow' : ''}`}
            style={{ ['--cb-orb-scale' as string]: orbScale }}
          />
        </div>
      </div>

      <div className="cb-guide-phase-block" aria-live="polite" aria-atomic="true">
        {paused ? (
          <p className="cb-guide-phase-label cb-fade-in">Paused</p>
        ) : phase?.label ? (
          <p key={`label-${tick?.phaseIdx}`} className="cb-guide-phase-label cb-fade-in">
            {phase.label}
          </p>
        ) : (
          <p className="cb-guide-phase-label" style={{ opacity: 0 }} aria-hidden="true">
            ·
          </p>
        )}

        {/* The hint line always takes its space (blank placeholder when empty) so the label
            above doesn't shift up and down as hints come and go between phases. */}
        {phase?.hint && !paused ? (
          <p key={`hint-${tick?.phaseIdx}`} className="cb-guide-phase-hint cb-fade-in">
            {phase.hint}
          </p>
        ) : (
          <p className="cb-guide-phase-hint" style={{ visibility: 'hidden' }} aria-hidden="true">
            ·
          </p>
        )}
      </div>

      <div className="cb-guide-phase-countdown" aria-hidden="true">
        {!paused && phaseCountdown > 0 && Boolean(phase?.label) && <span>{phaseCountdown}</span>}
      </div>

      <div className="cb-guide-bottom-bar">
        <button
          className={`cb-btn-secondary cb-guide-pause-btn ${paused ? 'cb-guide-pause-btn--resume' : ''}`}
          onClick={handlePause}
          aria-label={paused ? 'Resume session' : 'Pause session'}
        >
          {paused ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <polygon points="5,3 19,12 5,21" />
              </svg>
              Resume
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
              Pause
            </>
          )}
        </button>

        <button className="cb-guide-end-link" onClick={handleEndRequest}>
          End session
        </button>

        <a className="cb-guide-alts-link" href="https://www.renurture.org/post/breathingalts" target="_blank" rel="noopener noreferrer">
          Breathing making you feel worse?
        </a>
      </div>
    </div>
  );
}
